import { clsx, type ClassValue } from "clsx";
import { randomBytes } from "crypto";
import { twMerge } from "tailwind-merge";
import { ONE_DAY_MS } from "./constants";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// GO-738 item 2: slugs must be an alphanumeric + hyphen allowlist. The previous
// impl only lowercased and swapped single spaces, so metacharacters that pass the
// name filter (e.g. `'`, `;`, `--`) survived into the stored slug. Strip anything
// outside [a-z0-9-], collapse repeat hyphens, and trim — the result is guaranteed
// to match /^[a-z0-9-]*$/. Common names are unaffected ("My Workspace" -> "my-workspace").
export function slugify(name: string) {
  return name
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function generateId() {
  return crypto.randomUUID();
}

export function generateToken() {
  return randomBytes(32).toString("base64url");
}

export function addDays(date: Date, days: number) {
  return new Date(date.getTime() + days * ONE_DAY_MS);
}

/** Escapes SQL LIKE/ILIKE wildcard characters so they're treated as literals */
export function escapeLikeQuery(query: string): string {
  return query.replace(/[%_\\]/g, "\\$&");
}

export function formatCompactNumber(
  value: number | null | undefined,
  opts?: { prefix?: string; suffix?: string; nullDisplay?: string; compactThreshold?: number },
): string {
  if (value == null) return opts?.nullDisplay ?? "-";
  const p = opts?.prefix ?? "";
  const s = opts?.suffix ?? "";
  const threshold = opts?.compactThreshold ?? 1_000;
  if (value >= 1_000_000 && threshold <= 1_000_000) return `${p}${(value / 1_000_000).toFixed(1)}M${s}`;
  if (value >= 1_000 && threshold <= 1_000) return `${p}${(value / 1_000).toFixed(1)}K${s}`;
  return `${p}${value.toLocaleString("en-US")}${s}`;
}

export const getInitials = (name?: string) => {
  if (!name) return "";
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`;
  }
  return words[0][0];
};

// Date-only strings (YYYY-MM-DD) from external APIs parse as UTC midnight by default,
// causing a 1-day-off display bug for users in negative UTC offsets.
export function parseAPIDate(dateString: string): Date {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return new Date(`${dateString}T00:00:00`);
  }
  return new Date(dateString);
}

export function formatAPIDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export type CampaignStatsDates = {
  start_date?: string | null;
  end_date?: string | null;
};

/** Fill missing campaign dates from Wrango stats before computing the analysis window. */
export function enrichCampaignDatesForAnalysisWindow(
  campaigns: { id: string; startDate: Date | null; endDate: Date | null }[],
  statsMap?: Record<string, CampaignStatsDates | null> | null,
): { startDate: Date | null; endDate: Date | null }[] {
  return campaigns.map((c) => {
    const wrango = statsMap?.[c.id] ?? null;
    return {
      startDate:
        c.startDate ??
        (wrango?.start_date ? parseAPIDate(wrango.start_date) : null),
      endDate:
        c.endDate ?? (wrango?.end_date ? parseAPIDate(wrango.end_date) : null),
    };
  });
}

/** Earliest campaign start and latest campaign end across all report campaigns. */
export function computeAnalysisWindow(
  campaigns: { startDate: Date | null; endDate: Date | null }[],
): { start: string; end: string } | null {
  const dates = campaigns
    .map((c) => ({ start: c.startDate, end: c.endDate }))
    .filter((d): d is { start: Date; end: Date } => !!d.start && !!d.end);
  if (!dates.length) return null;
  const start = dates.reduce((a, b) => (a.start < b.start ? a : b)).start;
  const end = dates.reduce((a, b) => (a.end > b.end ? a : b)).end;
  const fmt = (d: Date) =>
    d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return { start: fmt(start), end: fmt(end) };
}
